import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import TimingsEditor from "@/pages/admin/TimingsEditor";

const FIELDS = [
  { key: "clinicName", label: "Clinic Name" },
  { key: "tagline", label: "Tagline" },
  { key: "doctorName", label: "Doctor Name" },
  { key: "qualifications", label: "Qualifications" },
  { key: "title", label: "Title / Designation" },
  { key: "experience", label: "Experience" },
  { key: "heroHeadline", label: "Homepage Headline", area: true },
  { key: "heroSupporting", label: "Homepage Supporting Text", area: true },
  { key: "aboutBio", label: "Doctor Biography", area: true },
  { key: "clinicDescription", label: "Clinic Description", area: true },
  { key: "address", label: "Address" },
  { key: "doctorMobile", label: "Doctor Mobile" },
  { key: "appointmentMobile", label: "Appointments Number" },
  { key: "email", label: "Email" },
  { key: "whatsapp", label: "WhatsApp Number (with country code)" },
  { key: "instagram", label: "Instagram Profile URL" },
];

export default function AdminContent() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setForm(r.data || {})).catch(() => setForm({})); }, []);

  const save = async () => {
    const bad = (form.timings || []).find((t) => !t.days?.length || !t.start || !t.end || t.start >= t.end);
    if (bad) return toast.error("Each timing slot needs at least one day and an end time after the start time");
    setSaving(true);
    try {
      await api.put("/admin/content", { data: form });
      toast.success("Website content updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;

  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-3 text-sm outline-none focus:border-terracotta";

  return (
    <div data-testid="admin-content-page" className="max-w-3xl">
      <h1 className="font-serif text-3xl text-charcoal">Website Content</h1>
      <p className="mt-1 text-sm text-taupe">Update key text and contact information shown across the website.</p>

      <div className="mt-8 space-y-5 rounded-3xl border border-soft-border bg-cream p-6 md:p-8">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="mb-1.5 block text-sm font-medium text-charcoal">{f.label}</label>
            {f.area ? (
              <textarea data-testid={`content-${f.key}`} rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            ) : (
              <input data-testid={`content-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            )}
          </div>
        ))}
        <TimingsEditor value={form.timings} onChange={(timings) => setForm({ ...form, timings })} />
        <button data-testid="content-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-cream hover:bg-terracotta-dark disabled:opacity-70">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </button>
      </div>
    </div>
  );
}
